import { FC, useState } from "react";
import { toast } from "react-toastify";
import {
  useAdminLocationControllerUpdateManyServiceAccess,
  useAdminLocationControllerUpdateLocationServiceAccess,
  useAdminLocationControllerDeleteMany,
} from "service";
import { ControlledSelect, ControlledSelectOption } from "ui";
import { queryClient } from "../../../query-client";
import { queryKeyFn } from "service/api/estabrookContext";
import { useDialogStore } from "../../../state/dialog-store";

export interface CustomerBulkActionsProps {
  locationIds: string[];
  resetSelections: () => void;
  selectedAddresses?: {
    locationId: string;
    customerId: string;
    address: string;
    customerServiceAccess: boolean;
  }[];
  onDownloadReport?: (locationIds: string[]) => void;
}

const customerBulkActionsOptions: ControlledSelectOption[] = [
  // {
  //   label: "Change service duration",
  //   value: "service-duration",
  // },
  {
    label: "Enable Service Access",
    value: "Active",
  },
  {
    label: "Disable Service Access",
    value: "Inactive",
  },
  {
    label: "Download Plow Report",
    value: "download-report",
  },
  {
    label: "Delete",
    value: "remove",
  },
];

const CustomerBulkActions: FC<CustomerBulkActionsProps> = (props) => {
  const { setDialog } = useDialogStore();
  const [selectedStatus, setSelectedStatus] = useState<
    "Active" | "Inactive" | null
  >(null);
  const { locationIds, resetSelections, selectedAddresses, onDownloadReport } =
    props;

  const isSingleAddress = locationIds.length === 1;
  const address =
    isSingleAddress && selectedAddresses && selectedAddresses.length > 0
      ? selectedAddresses[0].address
      : null;

  const handleLocationServiceAccessChange = (status: "Inactive" | "Active") => {
    // Check if any of the selected addresses belong to customers with disabled service access
    const addressesWithDisabledCustomerAccess =
      selectedAddresses?.filter(
        (addr) => addr.customerServiceAccess === false,
      ) || [];

    if (addressesWithDisabledCustomerAccess.length > 0) {
      if (addressesWithDisabledCustomerAccess.length === 1) {
        toast.error(
          `The customer for address "${addressesWithDisabledCustomerAccess[0].address}" has service access disabled. You are not allowed to edit the service access for this address. Please enable customer service access first.`,
        );
      } else {
        const addressList = addressesWithDisabledCustomerAccess
          .map((addr) => `"${addr.address}"`)
          .join(", ");
        toast.error(
          `${addressesWithDisabledCustomerAccess.length} selected address${addressesWithDisabledCustomerAccess.length > 1 ? "es" : ""} (${addressList}) belong${addressesWithDisabledCustomerAccess.length === 1 ? "s" : ""} to customer${addressesWithDisabledCustomerAccess.length > 1 ? "s" : ""} with service access disabled. Please enable customer service access first.`,
        );
      }
      return;
    }

    setSelectedStatus(status);
    const isActive = status === "Active";
    const serviceAccess = isActive;

    setDialog({
      title: `${isActive ? "Activate" : "Deactivate"} Address${!isSingleAddress ? "es" : ""}`,
      description: isActive
        ? `Are you sure you want to activate ${isSingleAddress ? address : locationIds.length} address${!isSingleAddress ? "es" : ""}?
${isSingleAddress ? `This will make the address available in its route and region again, and restore service access for the customer.` : "Each address will become available in its route and region again, and service access will be restored for the associated customers."}`
        : `Are you sure you want to deactivate ${isSingleAddress ? address : locationIds.length} address${!isSingleAddress ? "es" : ""}?
${isSingleAddress ? "This will remove it from its assigned route and region, and the customer will lose service access for this address." : "Each address will be removed from its assigned route and region, and customers will lose service access for those addresses."}`,
      confirmText: isActive ? "Activate" : "Deactivate",
      cancelText: "Cancel",
      sx: {
        "& .MuiDialog-container": {
          "& .MuiPaper-root": {
            width: "100%",
            maxWidth: "400px",
          },
        },
      },
      onConfirm: async (onClose) => {
        if (locationIds.length > 1) {
          // Use PATCH for multiple locations
          updateManyServiceAccess({
            body: locationIds.map((locationId) => ({
              id: locationId,
              serviceAccess,
            })),
          });
        } else {
          // Use PUT for single location
          updateSingleServiceAccess({
            pathParams: { locationId: locationIds[0] },
            body: { serviceAccess },
          });
        }
        onClose();
      },
    });
  };

  const handleManyLocationDelete = () => {
    const isSingleAddress = locationIds.length === 1;

    setDialog({
      title: "Delete Addresses",
      description:
        isSingleAddress && address
          ? `Are you sure you want to delete the address ${address}? This action cannot be undone.`
          : `Are you sure you want to delete these ${locationIds.length} addresses? This action cannot be undone.`,
      confirmText: "Delete",
      cancelText: "Cancel",
      sx: {
        "& .MuiDialog-container": {
          "& .MuiPaper-root": {
            width: "100%",
            maxWidth: "400px",
          },
        },
      },
      onConfirm: async (onClose) => {
        deleteManyLocations({
          body: locationIds.map((id) => ({ id })),
        });
        onClose();
      },
    });
  };

  const handleDownloadReport = () => {
    onDownloadReport?.(locationIds);
  };

  const handleBulkAction = (value: string) => {
    if (value === "Active" || value === "Inactive") {
      handleLocationServiceAccessChange(value);
    } else if (value === "download-report") {
      handleDownloadReport();
    } else {
      handleManyLocationDelete();
    }
  };

  const { mutate: updateManyServiceAccess } =
    useAdminLocationControllerUpdateManyServiceAccess({
      onSuccess: () => {
        toast.success(
          `${locationIds.length} ${locationIds.length > 1 ? "Addresses" : "Address"} Changed to ${selectedStatus || ""}`,
        );
        queryClient.invalidateQueries(
          queryKeyFn({
            path: "/api/v1/admin/customers-locations",
            operationId: "adminCustomerLocationControllerGetAll",
            variables: {},
          }),
        );
        // Invalidate individual location caches
        locationIds.forEach((locationId) => {
          queryClient.invalidateQueries(
            queryKeyFn({
              path: "/api/v1/admin/locations/{locationId}",
              operationId: "adminLocationControllerGetOne",
              variables: {
                pathParams: { locationId },
              },
            }),
          );
        });
        setSelectedStatus(null);
        resetSelections();
      },
    });

  const { mutate: updateSingleServiceAccess } =
    useAdminLocationControllerUpdateLocationServiceAccess({
      onSuccess: () => {
        toast.success(
          `${locationIds.length} ${locationIds.length > 1 ? "Addresses" : "Address"} Changed to ${selectedStatus || ""}`,
        );
        queryClient.invalidateQueries(
          queryKeyFn({
            path: "/api/v1/admin/customers-locations",
            operationId: "adminCustomerLocationControllerGetAll",
            variables: {},
          }),
        );
        // Invalidate individual location cache
        queryClient.invalidateQueries(
          queryKeyFn({
            path: "/api/v1/admin/locations/{locationId}",
            operationId: "adminLocationControllerGetOne",
            variables: {
              pathParams: { locationId: locationIds[0] },
            },
          }),
        );
        setSelectedStatus(null);
        resetSelections();
      },
    });

  const { mutate: deleteManyLocations } = useAdminLocationControllerDeleteMany({
    onSuccess: (response) => {
      const deletedCount = response.data?.deleted.length ?? 0;
      const failed = response.data?.failed ?? [];

      if (failed.length === 0) {
        toast.success(
          `Deleted ${deletedCount} ${deletedCount > 1 ? "Addresses" : "Address"} successfully!`,
        );
      } else if (deletedCount === 0) {
        toast.error(
          failed.length === 1
            ? failed[0].message
            : `${failed.length} addresses could not be deleted: ${failed
                .map((f) => f.message)
                .join(" ")}`,
        );
      } else {
        toast.error(
          `Deleted ${deletedCount} ${deletedCount > 1 ? "addresses" : "address"}, but ${failed.length} could not be deleted: ${failed
            .map((f) => f.message)
            .join(" ")}`,
        );
      }

      queryClient.invalidateQueries(
        queryKeyFn({
          path: "/api/v1/admin/customers-locations",
          operationId: "adminCustomerLocationControllerGetAll",
          variables: {},
        }),
      );
      resetSelections();
    },
  });

  return (
    <ControlledSelect
      sx={{ marginRight: "16px" }}
      placeholder="Bulk Actions"
      options={customerBulkActionsOptions}
      onChange={(value) => handleBulkAction(value)}
      disabled={locationIds.length === 0}
    />
  );
};

export default CustomerBulkActions;
